import { useAuthentication } from "@/contexts"
import { toHttpError } from "@/utils/api"
import { useQuery } from "@tanstack/react-query"

import { ApiQueryName } from "../../models/api"
import { Organization } from "../../models/organization"
import { useRenameQueryResult } from "../utils"
import { createQueryKey } from "../utils/createQueryKey"

// TODO: We should have/use a single fetchOrganizations function
// that takes all necessary parameters
export const fetchAuthorizedOrganizations = async (
  orgIds?: number[]
): Promise<Organization[]> => {
  try {
    const organizations = await Organization.where(
      orgIds ? { id: orgIds.map(String) } : {}
    )
      .order("name")
      .all()
      .then((response) => response.data)

    return organizations
  } catch (error: unknown) {
    throw toHttpError(error)
  }
}

/**
 * Returns the authorized organizations for the authenticated user
 *
 * @example
 * const { data: authorizedOrganizations } = useAuthorizedOrganizations()
 */
export const useAuthorizedOrganizations = () => {
  const { activeUser, isAuthenticated } = useAuthentication()

  // Cannot user useQueryKey at this time
  // useQueryKey requires a SettingsProvider
  const queryKey = createQueryKey(
    ApiQueryName.AuthorizedOrganizations,
    "getMany",
    null,
    {
      organizationIds: activeUser?.organizationIds,
    }
  )

  const queryFn = async (): Promise<Organization[]> =>
    await fetchAuthorizedOrganizations(activeUser?.organizationIds)

  const queryResult = useQuery({
    gcTime: Infinity,
    // Both isAuthenticated and activeUser could be undefined
    enabled: Boolean(isAuthenticated) && Boolean(activeUser),
    queryFn,
    queryKey,
    staleTime: Infinity,
  })

  return useRenameQueryResult(queryResult, ApiQueryName.AuthorizedOrganizations)
}
